import { Component } from '../../decorators/component.decorator';
import { UIElement } from '../ui-element';
import { Attribute } from '../../decorators/attribute.decorator';
import styles from './ui-slideview-main.css';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-slideview-main
 * @augments {UIElement}
 * @alias UISlideViewMain
 * @classdesc Represents a class for <code>ui-slideview-main</code> element. Meant to be used together with
 *      <code>ui-slideview</code> and <code>ui-slideview-detail</code> components to create a slideView interface.
 * @slot
 * @example
 * <ui-slideview-main>
 *     <h3>Main View</h3>
 *     <p target="target-1">This is the main view content.</p>
 * </ui-slideview-main>
 */
@Component({
    tag: 'ui-slideview-main',
    styles,
})
export class UISlideViewMain extends UIElement {
    @Attribute() target?: string;

    /**
     * Dispatches a custom event named 'slideview-open' to signal that a detail view should be opened.
     *
     * @fires CustomEvent#slideview-open
     * @property {string} detail.view - The ID of the target detail view.
     * @private
     */
    private sendViewChange(targetId: string): void {
        this.dispatchEvent(
            new CustomEvent('slideview-open', {
                detail: { view: targetId },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles activation events (click or keydown) on elements within the component.
     * Finds the closest ancestor element with a 'target' attribute, prevents the default event behavior,
     * and dispatches a 'slideview-open' event with the target ID to open the corresponding slide view.
     * @param {Event} event
     * @private
     */
    private handleTargetElementActivation = (event: Event) => {
        if (event instanceof KeyboardEvent && event.key !== 'Enter') {
            return;
        }
        const target = event.target as Element;
        const elementWithTarget = target.closest('[target]');

        if (elementWithTarget && elementWithTarget.hasAttribute('target')) {
            event.preventDefault();
            const targetId = elementWithTarget.getAttribute('target');
            if (targetId) {
                this.sendViewChange(targetId);
            }
        }
    };

    /**
     * Adds event listeners for click and keydown events.
     * @private
     */
    private addEventListeners() {
        this.addEventListener('click', this.handleTargetElementActivation);
        this.addEventListener('keydown', this.handleTargetElementActivation);
    }

    /**
     * Removes event listeners for click and keydown events.
     * @private
     */
    private removeEventListeners() {
        this.removeEventListener('click', this.handleTargetElementActivation);
        this.removeEventListener('keydown', this.handleTargetElementActivation);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}
